import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { CheckboxField } from '@/components/form/checkbox/Checkbox';
import { TsForm } from '@/components/form/Form';
import { TimeField } from '@/components/form/TimeInput';
import { getDayKey, secondsSinceMidnightToTimeString, timeStringToSecondsSinceMidnight } from '@/helpers/dateHelper';
import { useLocationDefaultTimes } from '@/pages/admin/locations/edit/useLocationDefaultTimes';
import { useLocationService } from '@/services/location/LocationService';
import { DefaultShiftTimeString } from '@/services/settings/systemSettings.types';
import { FC, useCallback, useMemo } from 'react';
import { Path, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useSelectedRoster } from '../providers/RosterOptionsService';
import { useWorkingRoster } from '../providers/WorkingRosterService';

interface RosterSettingsFormValues {
  rosterTimes: DefaultShiftTimeString;
  openingTimes: DefaultShiftTimeString;
  showOpeningHours: boolean;
}

const field = (name: Path<RosterSettingsFormValues>) => name;

export const RosterSettings: FC = () => {
  const selectedLocation = useLocationService(s => s.selectedLocation);
  if (!selectedLocation) throw new Error('No selected location found');

  const { date } = useParams<{ date: string }>();
  if (!date) throw new Error('Missing date');

  const { workingRoster, setWorkingRoster } = useWorkingRoster();
  const selectedRoster = useSelectedRoster();

  const { defaultRosterTimes, defaultOpeningTimes } = useLocationDefaultTimes(selectedLocation.id);

  const dayKey = getDayKey(new Date(date));
  const defaultRosterTimesForDay = defaultRosterTimes[dayKey];
  const defaultOpenTimesForDay = defaultOpeningTimes[dayKey];

  const parsedProperties = useMemo(() => {
    try {
      return JSON.parse(selectedRoster?.properties ?? '{}');
    } catch {
      return {};
    }
  }, [selectedRoster?.properties]);

  const showOpeningHoursFromProperties =
    parsedProperties.showOpeningHours !== undefined ? Boolean(parsedProperties.showOpeningHours) : undefined;

  const toFormTime = (rosterValue: number | undefined, defaultSecs: number) =>
    secondsSinceMidnightToTimeString(rosterValue ?? defaultSecs);

  const buildFormValues = (): RosterSettingsFormValues => ({
    rosterTimes: {
      start: toFormTime(workingRoster?.startTime, defaultRosterTimesForDay.start),
      end: toFormTime(workingRoster?.endTime, defaultRosterTimesForDay.end)
    },
    openingTimes: {
      start: toFormTime(workingRoster?.openTime, defaultOpenTimesForDay.start),
      end: toFormTime(workingRoster?.closeTime, defaultOpenTimesForDay.end)
    },
    showOpeningHours: showOpeningHoursFromProperties ?? !!(workingRoster?.openTime || workingRoster?.closeTime)
  });

  const methods = useForm<RosterSettingsFormValues>({
    defaultValues: buildFormValues(),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const showOpeningHours = methods.watch('showOpeningHours');

  const shifts = useMemo(
    () => workingRoster?.shifts?.map(s => ({ start: s.start ?? 0, end: s.end ?? 0 })) ?? [],
    [workingRoster?.shifts]
  );

  const validators = useMemo(() => {
    const getValues = methods.getValues;

    return {
      rosterStart: (value: string) => {
        const values = getValues();
        const start = timeStringToSecondsSinceMidnight(value);
        const end = timeStringToSecondsSinceMidnight(values.rosterTimes.end);
        const minShift = shifts.length ? Math.min(...shifts.map(s => s.start)) : null;

        if (start >= end) return 'Start time must be before end time.';
        if (minShift !== null && start > minShift) return 'Start time must be at or before the earliest shift.';
        return true;
      },

      rosterEnd: (value: string) => {
        const values = getValues();
        const end = timeStringToSecondsSinceMidnight(value);
        const start = timeStringToSecondsSinceMidnight(values.rosterTimes.start);
        const maxShift = shifts.length ? Math.max(...shifts.map(s => s.end)) : null;

        if (end <= start) return 'End time must be after start time.';
        if (maxShift !== null && end < maxShift) return 'End time must be at or after the latest shift.';
        return true;
      },

      openingStart: (value: string) => {
        const values = getValues();
        if (!values.showOpeningHours) return true;

        const open = timeStringToSecondsSinceMidnight(value);
        const close = timeStringToSecondsSinceMidnight(values.openingTimes.end);
        const rosterStart = timeStringToSecondsSinceMidnight(values.rosterTimes.start);

        if (open >= close) return 'Opening time must be before closing time.';
        if (open < rosterStart) return 'Opening time cannot be before roster start time.';
        return true;
      },

      openingEnd: (value: string) => {
        const values = getValues();
        if (!values.showOpeningHours) return true;

        const close = timeStringToSecondsSinceMidnight(value);
        const open = timeStringToSecondsSinceMidnight(values.openingTimes.start);
        const rosterEnd = timeStringToSecondsSinceMidnight(values.rosterTimes.end);

        if (close <= open) return 'Closing time must be after opening time.';
        if (close > rosterEnd) return 'Closing time cannot be after roster end time.';
        return true;
      }
    };
  }, [methods, shifts]);

  const onSave = useCallback(async () => {
    const isValid = await methods.trigger();
    if (!isValid) return;

    const values = methods.getValues();
    setWorkingRoster({
      ...workingRoster!,
      startTime: timeStringToSecondsSinceMidnight(values.rosterTimes.start),
      endTime: timeStringToSecondsSinceMidnight(values.rosterTimes.end),
      openTime: values.showOpeningHours ? timeStringToSecondsSinceMidnight(values.openingTimes.start) : undefined,
      closeTime: values.showOpeningHours ? timeStringToSecondsSinceMidnight(values.openingTimes.end) : undefined,
      showOpeningHours: values.showOpeningHours
    });
  }, [methods, workingRoster, setWorkingRoster]);

  return (
    <SubmissionErrorBoundary>
      <TsForm methods={methods} onSubmit={() => {}} className="py-2">
        <div className="space-y-8">
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-xl">Roster Hours</p>
              <p>These times represent the earliest and latest time a staff member can be rostered at.</p>
            </div>

            <div className="flex gap-4">
              <TimeField
                name={field('rosterTimes.start')}
                label="Start Time"
                rules={{ validate: validators.rosterStart }}
                onChangeCallback={onSave}
              />
              <TimeField
                name={field('rosterTimes.end')}
                label="End Time"
                rules={{ validate: validators.rosterEnd }}
                onChangeCallback={onSave}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="font-semibold text-xl">Opening Hours</p>
              <p>These times represent when the location is open to the public. They don't affect staff rostering.</p>
            </div>

            <CheckboxField
              className="flex-row"
              labelClassName="text-md"
              name={field('showOpeningHours')}
              label="Show opening hours"
              onChangeCallback={onSave}
            />

            {showOpeningHours && (
              <div className="flex gap-4">
                <TimeField
                  name={field('openingTimes.start')}
                  label="Opening Time"
                  rules={{ validate: validators.openingStart }}
                  onChangeCallback={onSave}
                />
                <TimeField
                  name={field('openingTimes.end')}
                  label="Closing Time"
                  rules={{ validate: validators.openingEnd }}
                  onChangeCallback={onSave}
                />
              </div>
            )}
          </div>
        </div>
      </TsForm>
    </SubmissionErrorBoundary>
  );
};
